import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/leuteliaApi";
import { SectionHead } from "./ModuleShell";
import { Upload, Loader2, Trash2, Globe, MapPin, ImagePlus, ArrowRight, Repeat } from "lucide-react";

export const SOURCE = {
  sito: { label: "Sito", icon: Globe, color: "#16493A" },
  google: { label: "Google", icon: MapPin, color: "#C2662E" },
  caricata: { label: "Caricata", icon: ImagePlus, color: "#C6902A" },
};

async function shrink(file) {
  if (!file.type.startsWith("image/")) throw new Error(`${file.name}: non è un'immagine`);
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) return file;
  const k = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
  if (k === 1 && file.size < 1.5e6) return file;
  const c = document.createElement("canvas"); c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
  c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
  const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.88));
  return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
}

export default function FotoUpload({ scope, uploads = [], counts = {}, googleLinked, onChange, analyses = [], pairs = [] }) {
  const replace = async (upload_id, replaces) => {
    try { await api.photoReplace({ tenant: scope.tenant, hotel: scope.hotel, upload_id, replaces: replaces || null }); toast.success(replaces ? "Segnata come rifacimento: dopo l'analisi vedrai il prima/dopo." : "Collegamento rimosso."); onChange?.(); }
    catch (e) { toast.error(e.message); }
  };
  const [busy, setBusy] = useState(null);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);

  const send = async (files) => {
    const list = Array.from(files || []).slice(0, 20);
    if (!list.length) return;
    let ok = 0;
    for (let i = 0; i < list.length; i++) {
      setBusy(`${i + 1} di ${list.length}`);
      try { await api.photoUpload(scope.tenant, scope.hotel, await shrink(list[i])); ok++; }
      catch (e) { toast.error(e.message); }
    }
    setBusy(null);
    if (ok) { toast.success(`${ok} foto caricat${ok === 1 ? "a" : "e"}. Ora puoi aggiornare l'analisi.`); onChange?.(); }
  };
  const remove = async (id) => {
    try { await api.photoUploadDelete({ tenant: scope.tenant, hotel: scope.hotel, upload_id: id }); toast.success("Foto rimossa."); onChange?.(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <section className="mb-8" data-testid="photo-sources">
      <SectionHead icon={Upload} title="Da dove arrivano le foto" sub="L'analisi copre tutte le immagini che l'ospite vede: sito, scheda Google e le foto che carichi tu." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {Object.entries(SOURCE).map(([k, s]) => (
          <div key={k} className="bg-white rounded-xl border border-brand-gold/25 p-4 flex items-center gap-3" data-testid={`photo-source-${k}`}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: s.color }}><s.icon size={18} className="text-white" /></span>
            <div className="min-w-0">
              <div className="font-serif text-[24px] leading-none text-brand-ink">{counts[k] || 0}</div>
              <div className="text-[12px] text-brand-muted mt-0.5">{k === "sito" ? "dal sito ufficiale" : k === "google" ? (googleLinked ? "dalla scheda Google" : "da Google · collega la scheda per attivarle") : "caricate dal direttore"}</div>
            </div>
          </div>
        ))}
      </div>

      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files); }}
        onClick={() => !busy && input.current?.click()} role="button" tabIndex={0} data-testid="photo-dropzone"
        className={`mt-4 rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${drag ? "border-brand-gold bg-brand-gold/10" : "border-brand-green/25 bg-white hover:bg-brand-cream/60"}`}>
        <input ref={input} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { send(e.target.files); e.target.value = ""; }} data-testid="photo-file-input" />
        {busy ? <div className="flex items-center justify-center gap-2 text-brand-green text-[13.5px] font-semibold"><Loader2 size={16} className="animate-spin" /> Carico la foto {busy}…</div>
          : <><div className="font-serif text-[18px] text-brand-ink">Carica le tue foto</div><p className="text-[12.5px] text-brand-muted mt-1">Trascina qui le immagini o clicca per sceglierle. Vengono ridotte automaticamente e analizzate insieme alle altre.</p></>}
      </div>

      {uploads.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2" data-testid="photo-uploads">
          {uploads.map((u) => (
            <div key={u.id} className="relative group rounded-lg overflow-hidden border border-brand-gold/25 bg-white" data-testid={`photo-upload-${u.id}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/photos/file/${scope.tenant}/${scope.hotel}/${u.id}`} alt="" className="w-full h-20 object-cover" loading="lazy" />
              <button onClick={() => remove(u.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-brand-maroon flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Rimuovi" data-testid={`photo-upload-remove-${u.id}`}><Trash2 size={12} /></button>
              <div className="px-1.5 py-1 text-[10px] text-brand-muted truncate">{u.filename}</div>
              <select value={u.replaces || ""} onChange={(e) => replace(u.id, e.target.value)} data-testid={`photo-upload-replaces-${u.id}`}
                className="w-full text-[10.5px] border-t border-brand-gold/20 bg-brand-cream/60 px-1 py-1 text-brand-ink">
                <option value="">Foto nuova</option>
                {analyses.filter((a) => !a.url.includes("/api/photos/file/")).map((a) => <option key={a.url} value={a.url}>Rifà: {a.subject} ({a.score}/10)</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
      {pairs.length > 0 && <PrimaDopo pairs={pairs} />}
    </section>
  );
}

function PrimaDopo({ pairs }) {
  return (
    <div className="mt-6" data-testid="photo-before-after">
      <SectionHead icon={Repeat} title="Prima e dopo" sub="Le foto rifatte a confronto con quelle che sostituiscono: il salto di voto è reale, foto per foto." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {pairs.map((p) => {
          const delta = p.before && p.after ? p.after.score - p.before.score : null;
          const col = delta == null ? "#6F6A5F" : delta > 0 ? "#2F6B4F" : delta < 0 ? "#B0452E" : "#C6902A";
          return (
            <div key={p.upload_id} className="bg-white rounded-xl border border-brand-gold/25 p-4" data-testid={`photo-pair-${p.upload_id}`}>
              <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
                {[["Prima", p.before_url, p.before], ["Dopo", p.after_url, p.after]].map(([label, url, a]) => (
                  <div key={label}>
                    <div className="text-[10.5px] font-bold uppercase tracking-wide text-brand-muted mb-1">{label}</div>
                    <div className="relative rounded-lg overflow-hidden"><img src={url} alt="" className="w-full h-36 object-cover" loading="lazy" />
                      <span className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ background: a ? (a.score >= 7 ? "#16493A" : a.score >= 5 ? "#C6902A" : "#B0452E") : "#6F6A5F" }}>{a ? `${a.score}/10` : "da analizzare"}</span></div>
                    <div className="text-[12px] text-brand-ink mt-1 line-clamp-2">{a?.subject || "—"}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mt-3 font-serif text-[22px]" style={{ color: col }} data-testid={`photo-pair-delta-${p.upload_id}`}>
                {delta == null ? <span className="text-[13px] text-brand-muted italic">Aggiorna l'analisi per vedere il salto di voto</span> : <><ArrowRight size={18} /> {delta > 0 ? "+" : ""}{delta} punti</>}
              </div>
              {p.after?.action && <p className="text-[12px] text-brand-muted mt-1">{p.after.summary}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
